<template>
  <div
    v-if="badges.length && !product.giftCard"
    class="product-badges"
    :class="{ [$class('loading')]: loading }"
  >
    <span
      v-for="(badge, index) of badges"
      :key="`${badge?.handle}-${index}`"
      class="product-badges__badge"
      :class="`product-badges__badge--${badge?.type}`"
    >
      <component :is="badge.icon" v-if="badge.icon" />

      <span
        class="text-mobile-paragraph-2xs-regular text-desktop-paragraph-xs-regular"
        aria-hidden="true"
        v-html="badge?.title"
      />
    </span>
  </div>
</template>

<script>
/**
 * Vue: Product badges (product-badges)
 * -----------------------------------------------------------------------------
 * Badges to display product and variant badges, stock, and campaigns.
 *
 * @param {Boolean} [discountPreview] - Show preview of promoted campaign.
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Formatted product object.
 * @param {String} [template] - Template location.
 * @param {Object} variant - Formatted variant object.
 *
 */
import { mapState } from 'pinia';

import IconTag from '~icons/general/tag.svg';

import { convertToHandle } from '~/helpers/convert';
import { computeDiscountLogic } from '~/helpers/vue';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'ProductBadges',

  components: {
    IconTag,
  },

  props: {
    bundleDisabled: {
      type: Boolean,
      default: false,
    },
    discountPreview: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    template: {
      type: String,
      default: 'product',
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      activeCampaign: 'active',
      discountCode: 'code',
      promotedCampaign: 'promoted',
      getXforYProducts: 'getXforYProducts',
    }),

    /**
     * Compute badges.
     * @returns {Array}
     */
    badges() {
      const limit = this.template === 'product' ? 2 : 1;

      if (this.loading && this.template === 'product') {
        return [...new Array(2)].map((_, index) => {
          return {
            handle: `${this.$string('accessibility.loading')} ${index}`,
            icon: false,
            title: '',
            type: 'black',
          };
        });
      }

      return [...this.redBadges, ...this.greyBadges, ...this.blackBadges].slice(
        0,
        limit,
      );
    },

    /**
     * Compute black (manual) badges.
     * @returns {Array}
     */
    blackBadges() {
      if (this.template === 'bought-together') {
        return [];
      }

      const badges = this.variant?.badges?.length
        ? this.variant?.badges
        : this.product?.badges;

      if (!badges) {
        return [];
      }

      return badges.map((title) => {
        return {
          handle: convertToHandle(title),
          icon: false,
          title: `<strong>${title}</strong>`,
          type: 'black',
        };
      });
    },

    /**
     * Compute discount logic.
     * @returns {Object}
     */
    discountLogic() {
      return computeDiscountLogic({
        discountPreview: this.discountPreview,
        onSale: this.variant?.compareAtPriceDiscount,
        product: this.product,
        variant: this.variant,
      });
    },

    /**
     * Compute grey (stock) badges.
     * @returns {Array}
     */
    greyBadges() {
      const badge = {
        handle: 'grey-badge',
        icon: false,
        type: 'grey',
      };

      switch (this.variant?.availableState) {
        case 'back-soon':
          badge.title = this.$string('product.badges.back_soon_html');
          break;
        case 'sold-out':
          badge.title = this.$string('product.badges.sold_out_html');
          break;
      }

      /**
       * Check if bundle is disabled and all items are sold out.
       * PDP: Show sold out badge.
       */
      if (
        this.product.type === 'bundle' &&
        (this.bundleDisabled ||
          this.product.bundle.some((item) => item.isSoldout === true))
      ) {
        badge.title = this.$string('product.badges.sold_out_html');
      }

      if (!badge.title) {
        return [];
      }

      return [badge];
    },

    /**
     * Compute promoted campaign red and green badges.
     * @returns {Boolean|Object}
     */
    promotedCampaignBadge() {
      if (!this.promotedCampaign) {
        return false;
      }

      const discount = this.getCampaignDiscount(this.discountLogic, 'promoted');

      if (!discount) {
        return false;
      }

      const options = this.getOptions(
        `-${discount}%`,
        this.promotedCampaign.code[0],
      );

      const title =
        this.template === 'product'
          ? this.$string('product.badges.promoted_campaign.long_html', options)
          : this.$string(
              'product.badges.promoted_campaign.short_html',
              options,
            );

      return {
        handle: 'promoted-campaign',
        icon: 'icon-tag',
        title,
        type: 'red',
      };
    },

    /**
     * Compute red (campaign/sale) badges.
     * - Only one red badge is rendered.
     * - Unless there is a XforY badge, then two red badges are rendered.
     * @returns {Array}
     */
    redBadges() {
      if (this.template === 'bought-together' || this.greyBadges.length) {
        return [];
      }

      return [
        this.redXforYBadge,
        this.redActiveCampaignBadge,
        this.redSaleBadge,
        this.promotedCampaignBadge,
      ]
        .filter(Boolean)
        .slice(0, 1);
    },

    /**
     * Compute active campaign red badge
     * @returns {Boolean|Object}
     */
    redActiveCampaignBadge() {
      if (!this.activeCampaign) {
        return false;
      }

      const discount = this.getCampaignDiscount(this.discountLogic, 'active');

      if (!discount) {
        return false;
      }

      const options = this.getOptions(`-${discount}%`, this.discountCode);

      return {
        handle: 'active-campaign',
        icon: 'icon-tag',
        title:
          this.template === 'product'
            ? this.$string('product.badges.active_campaign.long_html', options)
            : this.$string(
                'product.badges.active_campaign.short_html',
                options,
              ),
        type: 'red',
      };
    },

    /**
     * Compute red get X for Y badge.
     */
    redXforYBadge() {
      if (!this.activeCampaign) {
        return false;
      }

      const XForYCampaign = this.activeCampaign?.discounts.find(
        (campaign) => campaign.calculationType === 'buy-x-get-y',
      );
      const discount = this.getXforYProducts(this.discountLogic, 'active');

      if (!discount) {
        return false;
      }

      return {
        handle: 'x-for-y-campaign',
        icon: 'icon-tag',
        title:
          this.template === 'product'
            ? this.$string('product.badges.x_for_y_campaign.long_html', {
                replace: {
                  threshold: XForYCampaign.buyXGetYThreshold,
                  threshold_1: XForYCampaign.buyXGetYThreshold - 1,
                },
              })
            : this.$string('product.badges.x_for_y_campaign.short_html', {
                replace: {
                  threshold: XForYCampaign.buyXGetYThreshold,
                  threshold_1: XForYCampaign.buyXGetYThreshold - 1,
                },
              }),
        type: 'red',
      };
    },

    /**
     * Compute sale red badge.
     * @returns {Boolean|Object}
     */
    redSaleBadge() {
      if (
        !this.variant?.compareAtPriceDiscountRounded ||
        this.variant?.hideSalesBadge
      ) {
        return false;
      }

      const options = this.getOptions(
        `-${this.variant.compareAtPriceDiscountRounded}%`,
      );

      return {
        handle: 'sale-badge',
        icon: false,
        title:
          this.template === 'product'
            ? this.$string('product.badges.sale.long_html', options)
            : this.$string('product.badges.sale.short_html', options),
        type: 'red',
      };
    },
  },

  methods: {
    /**
     * Get string replacement options.
     * @param {String} discount - Discount string.
     * @param {String} [code] - Code string.
     * @returns {Object}
     */
    getOptions(discount, code) {
      return {
        decode: false,
        replace: {
          code,
          discount,
        },
      };
    },
  },
};
</script>
